/**
 * AdminSettingsPage - Configurações da plataforma (apenas admin)
 * Permite editar:
 *  - Perfil do admin (nome, telefone, palavra-passe)
 *  - Empresa (nome, morada)
 *  - Contactos (geral, suporte, negócios)
 *  - Dados bancários (BAI / IBAN / SWIFT)
 *  - Métodos de pagamento (Transferência, Multicaixa Express, É-Kwanza, Referência)
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
    Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { api, getApiErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
    Save, Lock, Loader2, Building2, Phone, Smartphone, QrCode, Receipt,
} from "lucide-react";
import AdminAgentsManager from "@/components/admin/AdminAgentsManager";
import AdminPlansManager from "@/components/admin/AdminPlansManager";

const METHOD_ICON = {
    transferencia: Building2,
    multicaixa_express: Smartphone,
    e_kwanza: QrCode,
    referencia_mc: Receipt,
};

export default function AdminSettingsPage() {
    const { user, setUser } = useAuth();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Profile
    const [profile, setProfile] = useState({ name: "", phone: "", country: "" });
    const [profileSaving, setProfileSaving] = useState(false);
    const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
    const [pwdSaving, setPwdSaving] = useState(false);

    useEffect(() => {
        if (user) {
            setProfile({
                name: user.name || "",
                phone: user.phone || "",
                country: user.country || "Angola",
            });
        }
    }, [user]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/settings");
            setSettings(data);
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const saveSection = async (section, payload) => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/settings", { [section]: payload });
            setSettings(data);
            toast.success("Configurações guardadas");
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    const saveProfile = async () => {
        setProfileSaving(true);
        try {
            const { data } = await api.put("/auth/me", profile);
            setUser(data);
            toast.success("Perfil atualizado");
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setProfileSaving(false);
        }
    };

    const changePassword = async () => {
        if (pwd.new_password.length < 6) {
            toast.error("Nova palavra-passe muito curta");
            return;
        }
        setPwdSaving(true);
        try {
            await api.post("/auth/change-password", pwd);
            toast.success("Palavra-passe alterada");
            setPwd({ current_password: "", new_password: "" });
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setPwdSaving(false);
        }
    };

    if (loading || !settings) {
        return (
            <div className="py-20 text-center text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> A carregar configurações...
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-settings-page">
            <div>
                <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                    Configurações
                </h1>
                <p className="text-muted-foreground">
                    Editar perfil, empresa, contactos, dados bancários e métodos de pagamento.
                </p>
            </div>

            <Tabs defaultValue="profile">
                <TabsList className="flex-wrap h-auto">
                    <TabsTrigger value="profile" data-testid="admin-settings-tab-profile">Perfil</TabsTrigger>
                    <TabsTrigger value="company" data-testid="admin-settings-tab-company">Empresa</TabsTrigger>
                    <TabsTrigger value="contacts" data-testid="admin-settings-tab-contacts">Contactos</TabsTrigger>
                    <TabsTrigger value="bank" data-testid="admin-settings-tab-bank">Dados Bancários</TabsTrigger>
                    <TabsTrigger value="methods" data-testid="admin-settings-tab-methods">Métodos de Pagamento</TabsTrigger>
                    <TabsTrigger value="plans" data-testid="admin-settings-tab-plans">Planos</TabsTrigger>
                    <TabsTrigger value="agents" data-testid="admin-settings-tab-agents">Agentes do Chat</TabsTrigger>
                    <TabsTrigger value="security" data-testid="admin-settings-tab-security">Segurança</TabsTrigger>
                </TabsList>

                {/* PROFILE */}
                <TabsContent value="profile" className="mt-4">
                    <Card className="glass-card p-5 rounded-2xl space-y-4">
                        <h3 className="font-display font-semibold">Perfil do administrador</h3>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <Label>Nome</Label>
                                <Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="admin-profile-name-input" />
                            </div>
                            <div>
                                <Label>Email (não editável)</Label>
                                <Input value={user?.email} disabled />
                            </div>
                            <div>
                                <Label>Telefone</Label>
                                <Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="admin-profile-phone-input" />
                            </div>
                            <div>
                                <Label>País</Label>
                                <Input value={profile.country} onChange={(e) => setProfile({ ...profile, country: e.target.value })} />
                            </div>
                        </div>
                        <Button onClick={saveProfile} disabled={profileSaving} className="btn-grad-purple text-white" data-testid="admin-profile-save-button">
                            {profileSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                            Guardar
                        </Button>
                    </Card>
                </TabsContent>

                {/* COMPANY */}
                <TabsContent value="company" className="mt-4">
                    <CompanyForm settings={settings} onSave={(p) => saveSection("company", p)} saving={saving} />
                </TabsContent>

                {/* CONTACTS */}
                <TabsContent value="contacts" className="mt-4">
                    <ContactsForm settings={settings} onSave={(p) => saveSection("contacts", p)} saving={saving} />
                </TabsContent>

                {/* BANK */}
                <TabsContent value="bank" className="mt-4">
                    <BankForm settings={settings} onSave={(p) => saveSection("bank", p)} saving={saving} />
                </TabsContent>

                {/* PAYMENT METHODS */}
                <TabsContent value="methods" className="mt-4">
                    <MethodsForm settings={settings} onSave={(p) => saveSection("payment_methods", p)} saving={saving} />
                </TabsContent>

                {/* PLANS */}
                <TabsContent value="plans" className="mt-4">
                    <AdminPlansManager />
                </TabsContent>

                {/* CHAT AGENTS */}
                <TabsContent value="agents" className="mt-4">
                    <AdminAgentsManager />
                </TabsContent>

                {/* SECURITY */}
                <TabsContent value="security" className="mt-4">
                    <Card className="glass-card p-5 rounded-2xl space-y-4">
                        <h3 className="font-display font-semibold">Alterar palavra-passe</h3>
                        <div>
                            <Label>Palavra-passe atual</Label>
                            <Input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="admin-current-password-input" />
                        </div>
                        <div>
                            <Label>Nova palavra-passe</Label>
                            <Input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="admin-new-password-input" />
                            <p className="text-xs text-muted-foreground mt-1">Mínimo 6 caracteres.</p>
                        </div>
                        <Button onClick={changePassword} disabled={pwdSaving} className="btn-grad-purple text-white" data-testid="admin-change-password-button">
                            {pwdSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
                            Alterar palavra-passe
                        </Button>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function CompanyForm({ settings, onSave, saving }) {
    const [form, setForm] = useState(settings.company || {});
    return (
        <Card className="glass-card p-5 rounded-2xl space-y-4">
            <h3 className="font-display font-semibold">Empresa</h3>
            <div>
                <Label>Nome comercial</Label>
                <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-company-name-input" />
            </div>
            <div>
                <Label>Nome legal</Label>
                <Input value={form.legal_name || ""} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} data-testid="admin-company-legal-input" />
            </div>
            <div>
                <Label>Morada</Label>
                <Textarea rows={2} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="admin-company-address-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <Label>Cidade</Label>
                    <Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                </div>
                <div>
                    <Label>País</Label>
                    <Input value={form.country || ""} onChange={(e) => setForm({ ...form, country: e.target.value })} />
                </div>
            </div>
            <Button onClick={() => onSave(form)} disabled={saving} className="btn-grad-purple text-white" data-testid="admin-company-save-button">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Guardar
            </Button>
        </Card>
    );
}

function ContactsForm({ settings, onSave, saving }) {
    const [form, setForm] = useState(settings.contacts || {});
    return (
        <Card className="glass-card p-5 rounded-2xl space-y-4">
            <h3 className="font-display font-semibold">Contactos</h3>
            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <Label>Email geral</Label>
                    <Input value={form.general_email || ""} onChange={(e) => setForm({ ...form, general_email: e.target.value })} data-testid="admin-contacts-general-email-input" />
                </div>
                <div>
                    <Label>Telefone geral</Label>
                    <Input value={form.general_phone || ""} onChange={(e) => setForm({ ...form, general_phone: e.target.value })} data-testid="admin-contacts-general-phone-input" />
                </div>
                <div>
                    <Label>Email suporte</Label>
                    <Input value={form.support_email || ""} onChange={(e) => setForm({ ...form, support_email: e.target.value })} data-testid="admin-contacts-support-email-input" />
                </div>
                <div>
                    <Label>WhatsApp suporte</Label>
                    <Input value={form.support_whatsapp || ""} onChange={(e) => setForm({ ...form, support_whatsapp: e.target.value })} data-testid="admin-contacts-support-whatsapp-input" />
                </div>
                <div>
                    <Label>Email negócios</Label>
                    <Input value={form.business_email || ""} onChange={(e) => setForm({ ...form, business_email: e.target.value })} data-testid="admin-contacts-business-email-input" />
                </div>
                <div>
                    <Label>Telefone negócios</Label>
                    <Input value={form.business_phone || ""} onChange={(e) => setForm({ ...form, business_phone: e.target.value })} data-testid="admin-contacts-business-phone-input" />
                </div>
            </div>
            <Button onClick={() => onSave(form)} disabled={saving} className="btn-grad-purple text-white" data-testid="admin-contacts-save-button">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Guardar
            </Button>
        </Card>
    );
}

function BankForm({ settings, onSave, saving }) {
    const [form, setForm] = useState(settings.bank || {});
    return (
        <Card className="glass-card p-5 rounded-2xl space-y-4">
            <h3 className="font-display font-semibold">Dados bancários para transferência</h3>
            <p className="text-sm text-muted-foreground">Estes dados aparecem aos clientes na página de subscrição.</p>
            <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                    <Label>Banco</Label>
                    <Input value={form.bank_name || ""} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="admin-bank-name-input" />
                </div>
                <div className="sm:col-span-2">
                    <Label>Titular</Label>
                    <Input value={form.holder || ""} onChange={(e) => setForm({ ...form, holder: e.target.value })} data-testid="admin-bank-holder-input" />
                </div>
                <div>
                    <Label>N.º conta</Label>
                    <Input value={form.account_number || ""} onChange={(e) => setForm({ ...form, account_number: e.target.value })} data-testid="admin-bank-account-input" />
                </div>
                <div>
                    <Label>NIB</Label>
                    <Input value={form.nib || ""} onChange={(e) => setForm({ ...form, nib: e.target.value })} data-testid="admin-bank-nib-input" />
                </div>
                <div className="sm:col-span-2">
                    <Label>IBAN</Label>
                    <Input value={form.iban || ""} onChange={(e) => setForm({ ...form, iban: e.target.value })} data-testid="admin-bank-iban-input" />
                </div>
                <div>
                    <Label>SWIFT</Label>
                    <Input value={form.swift || ""} onChange={(e) => setForm({ ...form, swift: e.target.value })} data-testid="admin-bank-swift-input" />
                </div>
                <div>
                    <Label>Prefixo referência</Label>
                    <Input value={form.reference_prefix || ""} onChange={(e) => setForm({ ...form, reference_prefix: e.target.value })} />
                </div>
            </div>
            <Button onClick={() => onSave(form)} disabled={saving} className="btn-grad-purple text-white" data-testid="admin-bank-save-button">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Guardar
            </Button>
        </Card>
    );
}

function MethodsForm({ settings, onSave, saving }) {
    const [methods, setMethods] = useState(settings.payment_methods || {});
    const update = (k, patch) => setMethods({ ...methods, [k]: { ...(methods[k] || {}), ...patch } });
    const ORDER = ["transferencia", "multicaixa_express", "e_kwanza", "referencia_mc"];

    return (
        <div className="space-y-4">
            <Card className="glass-card p-5 rounded-2xl">
                <h3 className="font-display font-semibold">Métodos de pagamento disponíveis</h3>
                <p className="text-sm text-muted-foreground mt-1">
                    Ative ou desative os métodos. As integrações automáticas Pay4all (Multicaixa Express, É-Kwanza, Referência) serão ligadas
                    quando recebermos as credenciais Pay4all. Por agora todos são validados manualmente pelo admin.
                </p>
            </Card>
            {ORDER.map((k) => {
                const m = methods[k] || {};
                const Icon = METHOD_ICON[k] || Building2;
                return (
                    <Card key={k} className="glass-card p-5 rounded-2xl space-y-3" data-testid={`admin-method-${k}`}>
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <span className="h-10 w-10 rounded-xl bg-[#6B21D9]/15 grid place-items-center text-[#A78BFA]">
                                    <Icon className="h-5 w-5" />
                                </span>
                                <div>
                                    <div className="font-semibold">{m.label || k}</div>
                                    <Badge variant="outline" className="text-xs mt-0.5">{k}</Badge>
                                </div>
                            </div>
                            <Switch
                                checked={!!m.active}
                                onCheckedChange={(v) => update(k, { active: v })}
                                data-testid={`admin-method-${k}-switch`}
                            />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                                <Label>Rótulo (label)</Label>
                                <Input value={m.label || ""} onChange={(e) => update(k, { label: e.target.value })} />
                            </div>
                            {["multicaixa_express", "e_kwanza"].includes(k) && (
                                <div>
                                    <Label>Telefone para envio</Label>
                                    <Input value={m.phone || ""} onChange={(e) => update(k, { phone: e.target.value })} />
                                </div>
                            )}
                        </div>
                        <div>
                            <Label>Descrição (instruções ao cliente)</Label>
                            <Textarea rows={2} value={m.description || ""} onChange={(e) => update(k, { description: e.target.value })} />
                        </div>
                    </Card>
                );
            })}
            <Button onClick={() => onSave(methods)} disabled={saving} className="btn-grad-purple text-white" data-testid="admin-methods-save-button">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Guardar métodos
            </Button>
        </div>
    );
}
